// teren3d.ts — terenul și săpătura, în scena 3D.
//
// Ce se desenează aici NU e model de calcul: e materie de pe șantier, ca
// placa pe sol. Geometria vine întreagă de la server (`sapatura.py`), care e
// și sursa volumelor din deviz — desenul și cifra nu pot spune două lucruri.
//
// Convenții:
//   * TERENUL e o prismă sub clădire, tăiată în straturile studiului
//     geotehnic, fiecare cu culoarea și cu densitatea lui de puncte. Nu se
//     desenează suprafețe pline: pământul e un NOR DE PUNCTE, mai des acolo
//     unde pământul e mai îndesat (densitatea de bază a clasei × γ/19), cu
//     culoarea variată puțin de la punct la punct — altfel un strat de 3 m
//     arată ca o felie de vopsea, nu ca pământ;
//   * din norul ăsta se SCOATE săpătura: un punct care ar cădea în groapă
//     sau într-un șanț nu se desenează. Golul se vede fiindcă e gol;
//   * SĂPĂTURA e portocalie și transparentă — nu e un material, e volumul
//     din care s-a scos pământ. Pereții ei sunt înclinați, cu panta pe care o
//     cere normativul pentru terenul traversat.
//
// Nimic de aici nu e clicabil: nu are proprietăți de calcul de arătat.

import * as THREE from "three";
import { numar, t } from "../i18n";
import { PAL, PAL_TEREN, PAL_SAPATURA, SemnTeren } from "./paletaDesen";
import { eticheta, fara_suprapunere, lider, marcaj, materialLinie,
         scurt, segmente } from "./hud3d";
import { FemRegimSapatura, FemSapatura } from "./tipuri";

// Bugetul total de puncte al scenei. Sub el norul arată ca o ceață rară;
// peste el, pe o stație modestă, orbitarea începe să se poticnească.
// Bugetul e mai mic decât la puncte simple: semnele sunt mai mari (o liniuță
// de argilă are 22 cm), deci aceeași senzație de plin se obține cu mai puține.
const BUGET_PUNCTE = 70000;

/** Generator determinist: aceeași scenă arată la fel la fiecare redesenare
 *  (altfel norul „fierbe" la orice schimbare de layer). */
function rnd32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

type Inel = [number, number][];
/** O groapă cu talpa și gura ei: groapa generală sau o adâncire locală sub
 *  radierul unui corp (vederea complexului) — aceeași formă, alte cote. */
type Groapa = { z_sus_m: number; z_jos_m: number; inel_jos: Inel[]; inel_sus: Inel[] };

/** Toate gropile blocului: cea generală și adâncirile locale. */
function gropile(sap: FemSapatura): Groapa[] {
  return [...(sap.groapa ? [sap.groapa] : []), ...(sap.gropi_locale ?? [])];
}

/** Șanțurile care chiar se sapă: în regimul «săpătură generală» analiza
 *  păstrează șanțurile cu geometria lor (planșa desenează tălpile în
 *  secțiune), dar ele stau în groapa săpată până la cota de fundare și n-au
 *  volum — nu sunt un gol în plus de desenat. */
function santuriDeSapat(sap: FemSapatura) {
  return (sap.santuri || []).filter((s) => s.volum_m3 > 0);
}

function inPoligon(inel: Inel, x: number, y: number) {
  let inauntru = false;
  for (let i = 0, j = inel.length - 1; i < inel.length; j = i++) {
    const [xi, yi] = inel[i];
    const [xj, yj] = inel[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi)
      inauntru = !inauntru;
  }
  return inauntru;
}

function distPeSegment(x: number, y: number, x1: number, y1: number,
                       x2: number, y2: number) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const l2 = dx * dx + dy * dy;
  if (l2 < 1e-12) return Math.hypot(x - x1, y - y1);
  let t = ((x - x1) * dx + (y - y1) * dy) / l2;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(x - (x1 + t * dx), y - (y1 + t * dy));
}

/** Inelele gropii interpolate pe câteva niveluri: talpa și gura au același
 *  număr de vârfuri, deci peretele înclinat se poate citi vârf cu vârf. */
function nivelurileGropii(g: Groapa, nr: number) {
  const niveluri: { z: number; inele: Inel[] }[] = [];
  for (let k = 0; k <= nr; k++) {
    const t = k / nr;
    niveluri.push({
      z: g.z_jos_m + t * (g.z_sus_m - g.z_jos_m),
      inele: g.inel_jos.map((jos, i) => {
        const sus = g.inel_sus[i] ?? jos;
        return jos.map(([x, y], v) => {
          const [xs, ys] = sus[v] ?? [x, y];
          return [x + t * (xs - x), y + t * (ys - y)] as [number, number];
        });
      }),
    });
  }
  return niveluri;
}

/** Testul „punctul ăsta a fost săpat?" — cu ieșiri ieftine pe cotă, fiindcă
 *  se cheamă de zeci de mii de ori. */
function facTestSapat(sap: FemSapatura) {
  const gropi = gropile(sap).map((g) => ({ g, niveluri: nivelurileGropii(g, 6) }));
  const santuri = santuriDeSapat(sap);
  const zSantMin = santuri.length
    ? -Math.max(...santuri.map((s) => s.Df_m)) : 0;
  // cutia în plan a șanțurilor, ca punctele de departe să nu plătească bucla
  let sx1 = Infinity, sy1 = Infinity, sx2 = -Infinity, sy2 = -Infinity;
  santuri.forEach((s) => {
    const raza = s.B_sus_m / 2;
    sx1 = Math.min(sx1, s.x1 - raza, s.x2 - raza);
    sx2 = Math.max(sx2, s.x1 + raza, s.x2 + raza);
    sy1 = Math.min(sy1, s.y1 - raza, s.y2 - raza);
    sy2 = Math.max(sy2, s.y1 + raza, s.y2 + raza);
  });

  return (x: number, y: number, z: number) => {
    for (const { g, niveluri } of gropi) {
      if (z < g.z_jos_m || z > g.z_sus_m) continue;
      const t = (z - g.z_jos_m) / Math.max(g.z_sus_m - g.z_jos_m, 1e-6);
      const niv = niveluri[Math.min(Math.round(t * 6), 6)];
      if (niv && niv.inele.some((inel) => inPoligon(inel, x, y))) return true;
    }
    if (z > 0 || z < zSantMin) return false;
    if (x < sx1 || x > sx2 || y < sy1 || y > sy2) return false;
    for (const s of santuri) {
      if (z < -s.Df_m) continue;
      const semi = s.B_m / 2 + (s.panta_m || 0) * (z + s.Df_m);
      if (distPeSegment(x, y, s.x1, s.y1, s.x2, s.y2) <= semi) return true;
    }
    return false;
  };
}

// ---------------------------------------------------------------------------
// Semnele straturilor: hașura geotehnică, ca sprite de punct
// ---------------------------------------------------------------------------
// Culoarea singură nu deosebește cinci straturi de argilă. Fiecare clasă
// primește SEMNUL ei — aceleași hașuri ca în secțiunile geotehnice (argilă =
// liniuțe orizontale, nisip = puncte, praf = liniuță + punct, rocă = cruci).
// Semnul e o textură mică, folosită ca sprite de punct: `THREE.Points` le
// desenează mereu cu fața la ecran, deci o liniuță rămâne orizontală din orice
// unghi, exact ca pe planșă.

const SEMNE = new Map<SemnTeren, THREE.Texture | null>();

function texturaSemn(semn: SemnTeren): THREE.Texture | null {
  if (SEMNE.has(semn)) return SEMNE.get(semn)!;
  const N = 64;
  const cv = document.createElement("canvas");
  cv.width = cv.height = N;
  // fără context 2D (jsdom, în teste) semnul nu se poate desena: punctele
  // rămân pătrate simple, iar scena se construiește la fel
  const c = cv.getContext("2d");
  if (!c) {
    SEMNE.set(semn, null);
    return null;
  }
  c.strokeStyle = "#fff";
  c.fillStyle = "#fff";
  c.lineCap = "round";
  const linie = (y: number, lat: number, gros: number) => {
    c.lineWidth = gros;
    c.beginPath();
    c.moveTo(N / 2 - lat / 2, y);
    c.lineTo(N / 2 + lat / 2, y);
    c.stroke();
  };
  const punct = (x: number, y: number, r: number) => {
    c.beginPath();
    c.arc(x, y, r, 0, Math.PI * 2);
    c.fill();
  };
  switch (semn) {
    case "puncte":
      punct(N / 2, N / 2, N * 0.30);
      break;
    case "linii":
      linie(N / 2, N * 0.82, N * 0.13);
      break;
    case "linii_duble":
      linie(N * 0.34, N * 0.82, N * 0.12);
      linie(N * 0.66, N * 0.52, N * 0.12);
      break;
    case "linie_punct":
      linie(N * 0.36, N * 0.74, N * 0.11);
      punct(N / 2, N * 0.70, N * 0.12);
      break;
    case "cercuri":
      c.lineWidth = N * 0.10;
      c.beginPath();
      c.arc(N / 2, N / 2, N * 0.30, 0, Math.PI * 2);
      c.stroke();
      break;
    case "cruci":
      c.lineWidth = N * 0.11;
      c.beginPath();
      c.moveTo(N * 0.18, N / 2); c.lineTo(N * 0.82, N / 2);
      c.moveTo(N / 2, N * 0.18); c.lineTo(N / 2, N * 0.82);
      c.stroke();
      break;
    case "amestec":
      // umplutura e amestec: și liniuță, și punct, și un colț de moloz
      linie(N * 0.30, N * 0.60, N * 0.10);
      punct(N * 0.30, N * 0.66, N * 0.09);
      c.lineWidth = N * 0.09;
      c.beginPath();
      c.moveTo(N * 0.58, N * 0.78); c.lineTo(N * 0.76, N * 0.58);
      c.lineTo(N * 0.86, N * 0.80); c.closePath();
      c.stroke();
      break;
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.anisotropy = 4;
  SEMNE.set(semn, tex);
  return tex;
}

// ---------------------------------------------------------------------------
// Săpătura: pereți înclinați, portocaliu transparent
// ---------------------------------------------------------------------------

function patrulater(out: number[], a: number[], b: number[], c: number[],
                    d: number[]) {
  out.push(...a, ...b, ...c, ...a, ...c, ...d);
}

/** Malurile gropii și pereții șanțurilor, ca două liste de triunghiuri
 *  SEPARATE: groapa are câteva fețe și suportă o transparență mai închisă,
 *  pe când șanțurile se suprapun la fiecare intersecție de axe, iar acolo
 *  transparențele se adună până la opac. */
function mesaSapaturii(sap: FemSapatura) {
  const p: number[] = [];
  // malurile gropii generale și ale adâncirilor locale de sub radiere
  gropile(sap).forEach((g) => {
    g.inel_jos.forEach((jos, i) => {
      const sus = g.inel_sus[i] ?? jos;
      for (let v = 0; v < jos.length; v++) {
        const w = (v + 1) % jos.length;
        patrulater(p,
          [jos[v][0], jos[v][1], g.z_jos_m],
          [jos[w][0], jos[w][1], g.z_jos_m],
          [sus[w]?.[0] ?? jos[w][0], sus[w]?.[1] ?? jos[w][1], g.z_sus_m],
          [sus[v]?.[0] ?? jos[v][0], sus[v]?.[1] ?? jos[v][1], g.z_sus_m]);
      }
    });
  });
  const q: number[] = [];
  santuriDeSapat(sap).forEach((s) => {
    const dx = s.x2 - s.x1;
    const dy = s.y2 - s.y1;
    const lg = Math.hypot(dx, dy) || 1;
    const nx = -dy / lg;
    const ny = dx / lg;
    const zj = -s.Df_m;
    const zs = 0;
    const bj = s.B_m / 2;
    const bs = s.B_sus_m / 2;
    const j0 = [s.x1 - nx * bj, s.y1 - ny * bj, zj];
    const j1 = [s.x2 - nx * bj, s.y2 - ny * bj, zj];
    const j2 = [s.x2 + nx * bj, s.y2 + ny * bj, zj];
    const j3 = [s.x1 + nx * bj, s.y1 + ny * bj, zj];
    const s0 = [s.x1 - nx * bs, s.y1 - ny * bs, zs];
    const s1 = [s.x2 - nx * bs, s.y2 - ny * bs, zs];
    const s2 = [s.x2 + nx * bs, s.y2 + ny * bs, zs];
    const s3 = [s.x1 + nx * bs, s.y1 + ny * bs, zs];
    patrulater(q, j0, j1, s1, s0);   // malurile lungi
    patrulater(q, j2, j3, s3, s2);
    patrulater(q, j1, j2, s2, s1);   // capetele
    patrulater(q, j3, j0, s0, s3);
    patrulater(q, j0, j3, j2, j1);   // talpa șanțului
  });
  return { groapa: p, santuri: q };
}

function conturSapaturii(sap: FemSapatura) {
  const linii: number[] = [];
  const inel = (pts: Inel, z: number) => {
    for (let v = 0; v < pts.length; v++) {
      const w = (v + 1) % pts.length;
      linii.push(pts[v][0], pts[v][1], z, pts[w][0], pts[w][1], z);
    }
  };
  gropile(sap).forEach((g) => {
    g.inel_jos.forEach((r) => inel(r, g.z_jos_m));
    g.inel_sus.forEach((r) => inel(r, g.z_sus_m));
  });
  santuriDeSapat(sap).forEach((s) => {
    const dx = s.x2 - s.x1;
    const dy = s.y2 - s.y1;
    const lg = Math.hypot(dx, dy) || 1;
    const nx = -dy / lg;
    const ny = dx / lg;
    [[s.B_m / 2, -s.Df_m], [s.B_sus_m / 2, 0]].forEach(([b, z]) => {
      linii.push(s.x1 - nx * b, s.y1 - ny * b, z,
                 s.x2 - nx * b, s.y2 - ny * b, z);
      linii.push(s.x1 + nx * b, s.y1 + ny * b, z,
                 s.x2 + nx * b, s.y2 + ny * b, z);
    });
  });
  return linii;
}

// ---------------------------------------------------------------------------
// Etichetele straturilor: unde stau
// ---------------------------------------------------------------------------

/** Cât iese liderul unei etichete din prismă, cel mult (m): dincolo de atât
 *  eticheta nu mai pare a stratului, ci a nimicului. */
export const LIDER_MAX_M = 12;
/** Ciotul orizontal al liderului până la scris, cel mult (m). */
export const CIOT_MAX_M = 5;
/** Scrisul etichetei la primul cadru, cel mult (m); pe ecran îl impune
 *  `scaleazaHud`, dar de el atârnă pasul dintre rânduri. */
export const SCRIS_MAX_M = 1.2;

/** Așezarea etichetelor straturilor: mărimea scrisului, PASUL dintre rânduri
 *  (`fara_suprapunere`) și cât ies liderii din prismă (`cot_m`, `capat_m`).
 *
 *  Scrisul e HUD — aceeași mărime pe ecran la orice zoom —, dar pasul și
 *  liderii sunt în metri și se văd. Scalate pe LUNGIMEA prismei (`lx`), pe un
 *  corp de 45 m dădeau 10 m de lider și rânduri la 2,4 m; pe complexul de
 *  400 m ieșeau lideri de 130 m și 13 rânduri pe 270 m de înălțime —
 *  etichetele straturilor pluteau în aer, departe de teren. Regula de aici:
 *  scrisul se ia după latura MICĂ și se plafonează; rândurile încap în
 *  ADÂNCIMEA pe care o acoperă (de la cota terenului până la rândul cel mai
 *  de jos), ca stiva să nu urce peste teren; liderii ies cel mult
 *  `LIDER_MAX_M` + `CIOT_MAX_M`. */
export function asezareaEtichetelor(lx: number, ly: number, adancime_m: number,
                                    nr: number) {
  const latura = Math.max(Math.min(lx, ly), 1);
  let inaltime_m = Math.min(Math.max(0.42, latura * 0.024), SCRIS_MAX_M);
  let pas_m = inaltime_m * 2.2;
  if (nr > 1 && adancime_m > 0 && pas_m * (nr - 1) > adancime_m) {
    pas_m = Math.max(adancime_m / (nr - 1), 0.5);
    inaltime_m = Math.max(Math.min(inaltime_m, pas_m / 2.2), 0.3);
  }
  return {
    inaltime_m, pas_m,
    cot_m: Math.min(Math.max(4.4, lx * 0.22), LIDER_MAX_M),
    capat_m: Math.min(Math.max(2.4, lx * 0.1), CIOT_MAX_M),
  };
}

// ---------------------------------------------------------------------------
// Grupul întreg
// ---------------------------------------------------------------------------

export interface CutiePlan {
  xmin: number; xmax: number; ymin: number; ymax: number;
}

/** Prisma de teren + săpătura, ca un singur grup (layerul „teren și
 *  săpătură" al scenei). `cutie` e amprenta în plan pe care se întinde
 *  terenul — clădirea, lărgită cu o margine, ca malurile să se vadă. */
export function grupTerenSapatura(sap: FemSapatura, cutie: CutiePlan) {
  const grup = new THREE.Group();
  const teren = sap.teren;
  const sapat = facTestSapat(sap);
  const { xmin, xmax, ymin, ymax } = cutie;
  const lx = Math.max(xmax - xmin, 1);
  const ly = Math.max(ymax - ymin, 1);

  // --- straturile: hașura lor, planurile de separație și etichetele --------
  if (teren?.straturi?.length) {
    const zCtn = teren.z_ctn_m;
    const zApa = typeof teren.z_apa_m === "number" ? teren.z_apa_m : null;
    const cx = (xmin + xmax) / 2;
    const cy = (ymin + ymax) / 2;
    const grosimi = teren.straturi.map(
      (s) => Math.max(s.z_sus_m - s.z_jos_m, 0));
    const cerute = teren.straturi.map((s, i) => {
      const pal = PAL_TEREN[s.clasa] ?? PAL_TEREN.coeziv_mijlocie;
      const gamma = typeof s.gamma_kn_m3 === "number" ? s.gamma_kn_m3 : 19;
      // pământul mai greu se desenează mai des: densitatea clasei × γ/19
      return pal.puncte_m3 * Math.min(Math.max(gamma / 19, 0.6), 1.4)
        * lx * ly * grosimi[i];
    });
    const total = cerute.reduce((a, b) => a + b, 0) || 1;
    const scara = Math.min(1, BUGET_PUNCTE / total);
    const albastru = new THREE.Color(PAL.apa);

    teren.straturi.forEach((s, i) => {
      if (grosimi[i] <= 0) return;
      const pal = PAL_TEREN[s.clasa] ?? PAL_TEREN.coeziv_mijlocie;
      const nr = Math.max(60, Math.round(cerute[i] * scara));
      const rnd = rnd32(1000 + i * 7919);
      // …și un pas de nuanță pe INDICELE stratului: două straturi din aceeași
      // clasă, unul peste altul, au același semn și aceeași culoare; pasul le
      // desparte, iar planul de separație de mai jos le taie limpede.
      const baza = new THREE.Color(pal.culoare);
      baza.offsetHSL(0, 0, (i % 4) * 0.05 - 0.075);
      const poz: number[] = [];
      const col: number[] = [];
      const c = new THREE.Color();
      // se încearcă de cel mult 2× mai multe ori: ce cade în săpătură se
      // aruncă, iar la o groapă mare stratul de sus rămâne rar — corect, aia
      // e chiar gaura
      for (let k = 0, incercari = 0; k < nr && incercari < nr * 2; incercari++) {
        const x = xmin + rnd() * lx;
        const y = ymin + rnd() * ly;
        const z = s.z_jos_m + rnd() * grosimi[i];
        if (sapat(x, y, z)) continue;
        poz.push(x, y, z);
        // culoarea variază ±12 % pe luminozitate: pământul nu e o vopsea…
        c.copy(baza).multiplyScalar(0.88 + rnd() * 0.24);
        // …iar sub nivelul apei pământul e SATURAT, deci trage spre albastru.
        // Așa are apa grosime: nu e o folie la o cotă, e tot ce e sub ea.
        if (zApa !== null && z < zApa) c.lerp(albastru, 0.42);
        col.push(c.r, c.g, c.b);
        k++;
      }
      if (!poz.length) return;
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position",
        new THREE.Float32BufferAttribute(poz, 3));
      geo.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
      const puncte = new THREE.Points(geo, new THREE.PointsMaterial({
        size: pal.marime_m, vertexColors: true, sizeAttenuation: true,
        map: texturaSemn(pal.semn) ?? undefined, transparent: true,
        alphaTest: 0.35, depthWrite: false,
      }));
      puncte.userData = { layer: "teren", strat: s.nume };
      grup.add(puncte);

      // VOLUMUL stratului: o felie translucidă în culoarea lui, pe toată
      // prisma. Fără ea, două straturi vecine se ating într-o ceață de puncte
      // și stratul e doar o linie de separație; cu ea, stratul e un corp cu
      // grosime. Opacitatea e mică fiindcă fețele se văd una prin alta
      // (`depthWrite: false`) și hașura de puncte trebuie să rămână citibilă.
      //
      // DOAR FEȚELE LATERALE: cu capacul de sus, felia de la cota
      // terenului trecea peste clădire ca o placă maro, iar tot ce stă sub
      // ±0,00 — subsolul, fundația — se vedea printr-o ceață. Stratul rămâne
      // un corp privit din lateral, ca într-o secțiune; de sus se vede în
      // groapă. Ordinea fețelor lui `BoxGeometry`: +x, −x, +y, −y, +z, −z.
      const lateral = new THREE.MeshBasicMaterial({
        color: baza, transparent: true, opacity: 0.16,
        side: THREE.DoubleSide, depthWrite: false,
      });
      const fara = new THREE.MeshBasicMaterial({ visible: false });
      const volum = new THREE.Mesh(
        new THREE.BoxGeometry(lx, ly, grosimi[i]),
        [lateral, lateral, lateral, lateral, fara, fara]);
      volum.position.set(cx, cy, s.z_jos_m + grosimi[i] / 2);
      volum.renderOrder = -1;                 // sub puncte și sub săpătură
      volum.userData = { layer: "teren", strat: s.nume };
      grup.add(volum);
      // …plus muchiile feliei, care dau grosimea la privirea din lateral
      const cadru = new THREE.LineSegments(
        new THREE.EdgesGeometry(new THREE.BoxGeometry(lx, ly, grosimi[i])),
        new THREE.LineBasicMaterial({
          color: baza, transparent: true, opacity: 0.35,
        }));
      cadru.position.set(cx, cy, s.z_jos_m + grosimi[i] / 2);
      grup.add(cadru);
    });

    // --- apa subterană: nivelul, ca linie, peste zona saturată -------------
    const jos = teren.straturi[teren.straturi.length - 1].z_jos_m;
    const areApa = zApa !== null && zApa < zCtn && zApa > jos;
    if (areApa) {
      const apa = new THREE.Mesh(
        new THREE.PlaneGeometry(lx, ly),
        new THREE.MeshBasicMaterial({
          color: albastru, transparent: true, opacity: 0.22,
          side: THREE.DoubleSide, depthWrite: false,
        }));
      apa.position.set(cx, cy, zApa!);
      grup.add(apa);
      const contur = new THREE.LineSegments(
        new THREE.EdgesGeometry(new THREE.PlaneGeometry(lx, ly)),
        new THREE.LineBasicMaterial({ color: albastru }));
      contur.position.set(cx, cy, zApa!);
      grup.add(contur);
    }

    // --- etichetele: un marcaj pe fața +X a fiecărui strat, un lider frânt
    // în unghi drept și textul «[de la–până la m] LITOLOGIA», de tipar — stilul
    // adnotării din `hud3d.ts`.
    // adâncimile cu cel mult două zecimale, fără zerouri de umplutură:
    // «0,3–1,3 m», nu «0,30–1,30 m»
    const ad = (v: number) => numar(v, {
      minimumFractionDigits: 1, maximumFractionDigits: 2 });
    const randuri: { z: number; text: string }[] =
      teren.straturi
        .map((s, i) => ({
          z: s.z_jos_m + grosimi[i] / 2,
          gros: grosimi[i],
          text: t("struct.teren.strat", {
            de_la: ad(s.de_la_m), pana_la: ad(s.pana_la_m),
            nume: scurt(s.nume, 44).toLocaleUpperCase("ro-RO"),
          }),
        }))
        .filter((r) => r.gros > 0);
    if (areApa)
      randuri.push({
        z: zApa!,
        text: t("struct.teren.apa", { n: ad(zCtn - zApa!) }),
      });
    randuri.sort((a, b) => b.z - a.z);          // de sus în jos
    // mărimea scrisului, pasul dintre rânduri și cât ies liderii: pe
    // adâncimea prismei, nu pe lungimea ei (v. `asezareaEtichetelor`)
    const adancimeRanduri = randuri.length
      ? zCtn - Math.min(...randuri.map((r) => r.z)) : 0;
    const asezare = asezareaEtichetelor(lx, ly, adancimeRanduri, randuri.length);
    const inaltimeText = asezare.inaltime_m;
    let cote = fara_suprapunere(randuri.map((r) => r.z), asezare.pas_m);
    // stiva se împinge în sus de la rândul de jos; unde straturile groase
    // lasă goluri mai mari decât pasul, vârful ei poate trece de cota
    // terenului — atunci coboară toată cu cât a trecut: etichetele stau la
    // prismă, nu deasupra ei
    const exces = cote.length ? cote[0] - zCtn : 0;
    if (exces > 0) cote = cote.map((c) => c - exces);
    // liderul iese orizontal din strat, departe de clădire ca textul să nu
    // se suprapună peste ea, cotește vertical până la rândul etichetei (când
    // stratul e prea subțire ca să și-l țină în dreptul lui), apoi un ciot
    // orizontal până la plăcuță
    const cot = xmax + asezare.cot_m;
    const capat = cot + asezare.capat_m;
    const linii: number[] = [];
    randuri.forEach((r, i) => {
      lider(linii, [xmax, cy, r.z], [cot, cy, r.z], [cot, cy, cote[i]],
            [capat, cy, cote[i]]);
      const m = marcaj(inaltimeText * 0.5);
      if (m) {
        m.position.set(xmax, cy, r.z);
        grup.add(m);
      }
      const et = eticheta(r.text, inaltimeText);
      if (et) {
        et.position.set(capat, cy, cote[i]);
        grup.add(et);
      }
    });
    if (linii.length) grup.add(segmente(linii, materialLinie(PAL.ink)));
  }

  // --- săpătura -------------------------------------------------------------
  const mese = mesaSapaturii(sap);
  // Transparența e ALTA pe fiecare: cu `depthWrite: false` fețele se adună,
  // iar rețeaua de șanțuri se suprapune la fiecare intersecție de axe — la
  // aceeași opacitate ca malul gropii ieșea un bloc portocaliu opac, adică
  // exact contrariul unui gol.
  ([["groapa", 0.22], ["santuri", 0.1]] as const).forEach(([cheie, opacity]) => {
    const poz = mese[cheie];
    if (!poz.length) return;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(poz, 3));
    geo.computeVertexNormals();
    const mal = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({
      color: new THREE.Color(PAL_SAPATURA), transparent: true, opacity,
      side: THREE.DoubleSide, depthWrite: false,
    }));
    mal.userData = { layer: "sapatura", parte: cheie };
    grup.add(mal);
  });
  const linii = conturSapaturii(sap);
  if (linii.length) {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(linii, 3));
    grup.add(new THREE.LineSegments(geo, new THREE.LineBasicMaterial({
      color: new THREE.Color(PAL_SAPATURA), transparent: true, opacity: 0.9,
    })));
  }
  return grup;
}

/** Textul care explică layerul, la hover pe rândul lui din panou. Aceleași
 *  cifre pe care le plătește devizul. */
export function titluTerenSapatura(sap: FemSapatura) {
  const nr = (v: number, z = 1) => numar(v, {
    minimumFractionDigits: z, maximumFractionDigits: z });
  const teren = sap.teren;
  // fiecare mal cu regimul LUI: groapa taie straturile de la suprafață,
  // șanțul pornește din talpa gropii, unde ele nu mai există
  const mal = (reg: FemRegimSapatura, ce: string) =>
    reg.vertical
      ? t("struct.teren.mal_vertical", {
          ce, adancime: nr(reg.adancime_m, 2), eticheta: reg.eticheta,
          h: nr(reg.h_vertical_m, 2),
        })
      : t("struct.teren.mal_taluz", {
          ce, panta: reg.panta_scrisa, sursa: reg.sursa,
        });
  const parti = [
    t("struct.teren.volum", { n: nr(sap.volum_m3) }) +
    (sap.volum_groapa_m3
      ? t("struct.teren.volum_detaliu", {
          groapa: nr(sap.volum_groapa_m3), santuri: nr(sap.volum_santuri_m3),
        })
      : ""),
    sap.regim_groapa ? mal(sap.regim_groapa, t("struct.teren.mal_gropii")) : null,
    // fără șanțuri (radier) malul șanțului nu există: regimul e al gropii
    santuriDeSapat(sap).length
      ? mal(sap.regim, t(sap.groapa
          ? "struct.teren.mal_santurilor" : "struct.teren.mal_sapaturii"))
      : null,
    // săpătura generală: ce rămâne gol în groapă se pune la loc
    sap.umplutura?.umplutura_m3
      ? t("struct.teren.umplutura", { n: nr(sap.umplutura.umplutura_m3) })
      : null,
    // vederea complexului: adâncirile sub radiere și cât ar fi ieșit pe corpuri
    sap.gropi_locale?.length
      ? t("struct.teren.locale", { n: sap.gropi_locale.length,
                                  volum: nr(sap.volum_locale_m3 ?? 0) })
      : null,
    typeof sap.volum_corpuri_m3 === "number"
      ? t("struct.teren.corpuri_suma", { n: nr(sap.volum_corpuri_m3) })
      : null,
    sap.spatiu_lucru_m > 0
      ? t("struct.teren.spatiu_lucru", {
          n: nr(sap.spatiu_lucru_m, 2),
          latimi: sap.latimi_sant_m
            .map(([b, l]) => `${nr(b, 2)} → ${nr(l, 2)} m`).join(", "),
        })
      : null,
    sap.volum_taluz_m3 > 0
      ? t("struct.teren.taluz", {
          total: nr(sap.volum_m3), taluz: nr(sap.volum_taluz_m3),
        })
      : null,
    sap.regim.articol,
    sap.spatiu_lucru_m > 0 ? sap.articol_spatiu : null,
    teren?.straturi?.length
      ? t("struct.teren.straturi", {
          n: teren.straturi.length,
          adancime: nr(teren.adancime_studiu_m, 2),
        })
      : t("struct.teren.fara_straturi"),
    ...(sap.regim_groapa?.note || []),
    ...(sap.regim.note || []),
  ];
  return parti.filter(Boolean).join(" · ");
}
